/**
 * Where the document screens live, and how they reach each other.
 *
 * These four routes are registered in `src/router.tsx` — `/documents/new`,
 * `/documents/review`, `/documents/$nodeId`, and `/documents/$nodeId/versions`,
 * all inside the `/_authed` gate. That is what lets everything here be a router
 * `Link` with a `to` the compiler checks against the tree: a typo in a path is
 * a build failure rather than a 404 somebody finds later.
 *
 * This file used to hold an anchor that pushed onto the router's history,
 * because none of the routes existed and `<Link to="/documents/$nodeId">` was
 * therefore a type error. It was scaffolding with a stated deletion condition,
 * the condition is met, and what is left is the two things a `Link` does not
 * give you for free: the button skin, and the `$nodeId` this screen is about.
 */

import { Link, useParams, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '../../components/ui/cn.ts';

/**
 * A link to a document that wears the kit's button skin.
 *
 * Deliberately not `components/ui/button.tsx`'s `LinkButton`, which is a React
 * Aria `Link` around a bare `href` — no `RouterProvider` is installed, so that
 * one is a full page load. For a link between two screens of the same document
 * that would discard the query cache that just fetched it.
 */
export function DocumentLinkButton({
  nodeId,
  to,
  children,
  className,
}: {
  nodeId: string;
  to: '/documents/$nodeId' | '/documents/$nodeId/versions';
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link
      to={to}
      params={{ nodeId }}
      className={cn(
        'inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border',
        'border-primary bg-primary px-3.5 text-sm font-semibold whitespace-nowrap text-secondary',
        'no-underline shadow-xs transition-colors hover:bg-primary-hover',
        className,
      )}
    >
      {children}
    </Link>
  );
}

/**
 * The `$nodeId` this screen is about.
 *
 * `from` names the `/$nodeId` route rather than either of its children, because
 * both the document page and the version history are matched *through* it —
 * one `from` answers for both, and naming a child would make this hook usable
 * on only one of the two screens it is used on.
 */
export function useDocumentNodeId(): string {
  return useParams({ from: '/_authed/_employee/documents/$nodeId' }).nodeId;
}

/**
 * Query-string prefills for the creation form.
 *
 * Read through the route's own search schema, which declares both keys — see
 * `newDocumentRoute` in `src/router.tsx`. A prefill is a convenience and both
 * keys are optional: the form asks for the space regardless, and an unknown id
 * fails at the API rather than here.
 */
export function useCreatePrefill(): { spaceId: string; parentNodeId: string } {
  const search = useSearch({ from: '/_authed/_employee/documents/new' });
  return { spaceId: search.spaceId ?? '', parentNodeId: search.parentNodeId ?? '' };
}
